import React, { useState, useMemo, useRef, useCallback } from "react";
import {
  Code,
  X,
  Plus,
  Braces,
  ChevronRight,
  Package,
  ChevronDown,
} from "lucide-react";
import { WorkflowNodeModal } from "../../../common/shared/WorkflowNodeModal";
import syntaxHighlight from "../../../utils/jsonViewer";

// VS Code Light theme colors
const THEME = {
  keyword: "#0000ff",
  string: "#a31515",
  function: "#795e26",
  variable: "#001080",
  comment: "#008000",
  number: "#098658",
  bracket: "#0431fa",
  default: "#000000",
};

// Token-based highlighting to avoid regex overlap issues
const tokenize = (code: string, lang: string): { type: string; value: string }[] => {
  const tokens: { type: string; value: string }[] = [];
  let remaining = code;

  const jsKeywords = new Set([
    "const", "let", "var", "function", "return", "if", "else", "for", "while",
    "do", "switch", "case", "break", "continue", "new", "this", "class",
    "extends", "import", "export", "default", "from", "async", "await",
    "try", "catch", "finally", "throw", "typeof", "instanceof", "in", "of",
    "true", "false", "null", "undefined", "void", "delete", "yield",
  ]);

  const pyKeywords = new Set([
    "def", "return", "if", "elif", "else", "for", "while", "break",
    "continue", "class", "import", "from", "as", "try", "except",
    "finally", "raise", "with", "yield", "lambda", "pass", "assert",
    "True", "False", "None", "and", "or", "not", "in", "is", "global",
    "nonlocal", "del", "async", "await",
  ]);

  const keywords = lang === "python" ? pyKeywords : jsKeywords;

  while (remaining.length > 0) {
    // Comments
    if (lang === "javascript" && remaining.startsWith("//")) {
      const end = remaining.indexOf("\n");
      const comment = end === -1 ? remaining : remaining.slice(0, end);
      tokens.push({ type: "comment", value: comment });
      remaining = remaining.slice(comment.length);
      continue;
    }
    if (lang === "python" && remaining.startsWith("#")) {
      const end = remaining.indexOf("\n");
      const comment = end === -1 ? remaining : remaining.slice(0, end);
      tokens.push({ type: "comment", value: comment });
      remaining = remaining.slice(comment.length);
      continue;
    }

    // Strings
    if (remaining[0] === '"' || remaining[0] === "'" || remaining[0] === "`") {
      const quote = remaining[0];
      let i = 1;
      while (i < remaining.length && remaining[i] !== quote) {
        if (remaining[i] === "\\") i++;
        i++;
      }
      const str = remaining.slice(0, i + 1);
      tokens.push({ type: "string", value: str });
      remaining = remaining.slice(str.length);
      continue;
    }

    // Numbers
    const numMatch = remaining.match(/^(\d+\.?\d*)/);
    if (numMatch && (tokens.length === 0 || !/[a-zA-Z_$]/.test(tokens[tokens.length - 1]?.value?.slice(-1) || ""))) {
      tokens.push({ type: "number", value: numMatch[1] });
      remaining = remaining.slice(numMatch[1].length);
      continue;
    }

    // Words (keywords, functions, variables)
    const wordMatch = remaining.match(/^([a-zA-Z_$][a-zA-Z0-9_$]*)/);
    if (wordMatch) {
      const word = wordMatch[1];
      const nextChar = remaining[word.length] || "";
      if (keywords.has(word)) {
        tokens.push({ type: "keyword", value: word });
      } else if (nextChar === "(") {
        tokens.push({ type: "function", value: word });
      } else {
        tokens.push({ type: "variable", value: word });
      }
      remaining = remaining.slice(word.length);
      continue;
    }

    // Brackets
    if ("()[]{}".includes(remaining[0])) {
      tokens.push({ type: "bracket", value: remaining[0] });
      remaining = remaining.slice(1);
      continue;
    }

    // Default (operators, whitespace, etc.)
    tokens.push({ type: "default", value: remaining[0] });
    remaining = remaining.slice(1);
  }

  return tokens;
};

const highlightCode = (code: string, lang: string): string => {
  if (!code) return "";
  return tokenize(code, lang)
    .map(({ type, value }) => {
      const escaped = value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
      const color = THEME[type as keyof typeof THEME] || THEME.default;
      if (type === "default") return escaped;
      return `<span style="color: ${color}">${escaped}</span>`;
    })
    .join("");
};

interface CodeEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  code: string;
  setCode: (code: string) => void;
  language: string;
  setLanguage: (lang: string) => void;
  inputs: Record<string, string>;
  setInputs: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  nodeId: string;
  dataOutputs?: any;
}

export const CodeEditorModal: React.FC<CodeEditorModalProps> = ({
  isOpen,
  onClose,
  code,
  setCode,
  language,
  inputs,
  setInputs,
  nodeId,
  dataOutputs,
}) => {
  const [draggedTargetInput, setDraggedTargetInput] = useState<string | null>(null);
  const [showAllInputs, setShowAllInputs] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const highlightedCode = useMemo(() => {
    return highlightCode(code, language) + (code.endsWith("\n") ? "\n " : " ");
  }, [code, language]);

  const jsExample = `// Add two numbers
const a = inputs.arg1 || 0;
const b = inputs.arg2 || 0;

return a + b;`;

  const loadExample = useCallback(() => {
    setCode(jsExample);
  }, [setCode]);

  const addInput = useCallback(() => {
    setInputs((prev) => {
      const newKey = `arg${Object.keys(prev).length + 1}`;
      return { ...prev, [newKey]: "" };
    });
  }, [setInputs]);

  const removeInput = useCallback((key: string) => {
    setInputs((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, [setInputs]);

  const updateInputKey = useCallback((oldKey: string, newKey: string) => {
    if (!newKey || oldKey === newKey) return;
    setInputs((prev) => {
      const val = prev[oldKey];
      const next = { ...prev };
      delete next[oldKey];
      next[newKey] = val;
      return next;
    });
  }, [setInputs]);

  const handleDrop = useCallback((e: React.DragEvent, inputKey: string) => {
    e.preventDefault();
    e.stopPropagation();
    let variable = e.dataTransfer.getData("text/plain");
    if (variable && !variable.startsWith("{{")) {
      variable = `{{${variable}}}`;
    }
    if (variable) {
      setInputs((prev) => ({ ...prev, [inputKey]: variable }));
    }
    setDraggedTargetInput(null);
  }, [setInputs]);

  const handleDragOver = useCallback((e: React.DragEvent, inputKey: string) => {
    e.preventDefault();
    e.stopPropagation();
    setDraggedTargetInput(inputKey);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDraggedTargetInput(null);
  }, []);


  const inputEntries = Object.entries(inputs);
  const hasOutputs = dataOutputs !== undefined && dataOutputs !== null;

  return (
    <WorkflowNodeModal
      isOpen={isOpen}
      onClose={onClose}
      nodeId={nodeId}
      title="Code Node - JavaScript editor"
      icon={<Code className="w-5 h-5 text-blue-600" />}
      themeColor="blue"
      nodeDocsType="CODE"
      validationStatus={{
        isValid: !!code,
        errors: !code ? ["Code is required"] : [],
      }}
      nodeData={{ outputs: dataOutputs }}
      nodeIcon={<Code className="w-6 h-6 text-gray-400" />}
      emptyOutputsText="No outputs yet"
      renderOutputsSidebar={() => (
        <div className="w-[420px] shrink-0 bg-gray-50 p-4 overflow-y-auto flex flex-col h-full [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-200 [&::-webkit-scrollbar-thumb]:rounded-full scrollbar-">
          <h3 className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-3">
            Execution Output
          </h3>
          <div className="space-y-3">
            <div className="bg-white border p-3 rounded-lg shadow-sm">
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">Status</span>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${hasOutputs ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                  {hasOutputs ? "SUCCESS" : "IDLE"}
                </span>
              </div>
            </div>
            {hasOutputs ? (
              typeof dataOutputs === "object" && !Array.isArray(dataOutputs) ? (
                Object.entries(dataOutputs).map(([key, value]: [string, any]) => (
                  <div key={key} className="bg-white border rounded-lg shadow-sm overflow-hidden">
                    <div className="px-3 py-2 flex items-center justify-between border-b border-gray-100 bg-gray-50">
                      <span className="text-[10px] font-bold text-gray-500 uppercase">{key}</span>
                      <span className="text-[10px] text-gray-400 font-mono">{Array.isArray(value) ? "Array" : typeof value}</span>
                    </div>
                    <div className="p-2 overflow-x-auto">
                      <pre className="text-xs font-mono break-all whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: syntaxHighlight(value) }} />
                    </div>
                  </div>
                ))
              ) : (
                <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
                  <div className="px-3 py-2 flex items-center justify-between border-b border-gray-100 bg-gray-50">
                    <span className="text-[10px] font-bold text-gray-500 uppercase">Result</span>
                    <span className="text-[10px] text-gray-400 font-mono">{Array.isArray(dataOutputs) ? "Array" : typeof dataOutputs}</span>
                  </div>
                  <div className="p-2 overflow-x-auto">
                    <pre className="text-xs font-mono break-all whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: syntaxHighlight(dataOutputs) }} />
                  </div>
                </div>
              )
            ) : (
              <div className="text-center py-10 border-2 border-dashed border-gray-200 rounded-lg">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Package className="w-4 h-4 text-gray-400" />
                </div>
                <p className="text-xs text-gray-400 italic">No outputs generated yet.</p>
              </div>
            )}
          </div>
        </div>
      )}
    >
      {/* Full-height flex container for center column */}
      <div className="flex flex-col h-full overflow-hidden">
        {/* ── Input Mapping Bar ─────────────────────────────────── */}
        <div className="shrink-0 border-b border-gray-200 bg-gray-50/80 px-4 py-3">
          <div className="flex items-center justify-between mb-2.5">
            <div className="flex items-center gap-2">
              <div className="p-1 bg-cyan-100 rounded text-cyan-600">
                <Braces className="w-3.5 h-3.5" />
              </div>
              <span className="text-xs font-semibold text-gray-800">Input Mapping</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-white border border-gray-200 rounded-sm text-[11px] font-medium text-gray-700">
                <div className="w-1.5 h-1.5 rounded-full bg-yellow-400" />
                JavaScript
              </div>
              <button onClick={loadExample} className="text-[11px] px-2.5 py-1 bg-white border border-gray-200 text-gray-600 rounded-sm hover:bg-gray-50 font-medium flex items-center gap-1 transition-colors">
                <Package className="w-3 h-3 text-cyan-500" /> Example
              </button>
              <button onClick={addInput} className="text-[11px] px-2.5 py-1 bg-cyan-600 text-white rounded-sm hover:bg-cyan-700 font-medium flex items-center gap-1 transition-colors">
                <Plus className="w-3 h-3" /> Input
              </button>
            </div>
          </div>

          <div>
            {inputEntries.length === 0 ? (
              <div className="text-[11px] text-gray-400 text-center py-3 border border-dashed border-gray-200 rounded-md bg-white">
                No inputs. Click "Input" or drag variables from the left.
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                  {(showAllInputs ? inputEntries : inputEntries.slice(0, 4)).map(([key, value]) => (
                    <div key={key} className="group flex items-center bg-white rounded-md border border-gray-200 hover:border-cyan-300 transition-all overflow-hidden">
                      <input
                        value={key}
                        onChange={(e) => updateInputKey(key, e.target.value)}
                        className="w-16 text-[11px] px-2 py-1.5 border-r border-gray-200 focus:outline-none font-semibold text-gray-700 bg-gray-50"
                        placeholder="arg"
                      />
                      <ChevronRight className="w-2.5 h-2.5 text-gray-300 mx-0.5 flex-shrink-0" />
                      <div
                        className={`flex-1 min-w-0 transition-colors ${draggedTargetInput === key ? "bg-cyan-50" : ""}`}
                        onDragOver={(e) => handleDragOver(e, key)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, key)}
                      >
                        <input
                          value={value}
                          onChange={(e) => setInputs((prev) => ({ ...prev, [key]: e.target.value }))}
                          className="w-full text-[11px] px-2 py-1.5 bg-transparent border-none outline-none font-mono text-cyan-700 placeholder:text-gray-300 truncate"
                          placeholder="Drag variable or type"
                        />
                      </div>
                      <button onClick={() => removeInput(key)} className="p-1 text-gray-300 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all">
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  ))}
                </div>
                {inputEntries.length > 4 && (
                  <button
                    onClick={() => setShowAllInputs(!showAllInputs)}
                    className="w-full mt-1.5 py-1 text-[10px] font-medium text-cyan-600 hover:text-cyan-800 hover:bg-cyan-50 rounded-md transition-colors flex items-center justify-center gap-1"
                  >
                    <ChevronDown className={`w-3 h-3 transition-transform ${showAllInputs ? "rotate-180" : ""}`} />
                    {showAllInputs ? "Show less" : `Show all ${inputEntries.length} inputs`}
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── Code Editor (fills remaining height) ─────────────── */}
        <div className="flex-1 min-h-0 flex flex-col bg-white">
          {/* Editor tab bar */}
          <div className="shrink-0 flex items-center justify-between px-4 py-1.5 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
              </div>
              <span className="text-[11px] text-gray-500 font-mono">main.js</span>
            </div>
            <span className="text-[9px] text-cyan-600 uppercase tracking-wide font-bold">
              {inputEntries.length} input(s)
            </span>
          </div>

          {/* Editor area */}
          <div
            className="code-editor-area relative flex-1 min-h-0 overflow-auto bg-white [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-corner]:bg-transparent"
          >
            <div className="relative min-h-full">
              {/* Syntax highlight layer */}
              <pre
                aria-hidden="true"
                className="p-4 m-0 font-mono text-sm leading-relaxed whitespace-pre pointer-events-none"
                style={{ fontFamily: "'Fira Code', 'Cascadia Code', Consolas, monospace" }}
                dangerouslySetInnerHTML={{ __html: highlightedCode }}
              />
              {/* Editable textarea - overlays the pre exactly */}
              <textarea
                ref={textareaRef}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="absolute inset-0 w-full h-full p-4 m-0 font-mono text-sm leading-relaxed whitespace-pre bg-transparent border-none outline-none resize-none overflow-hidden caret-gray-800"
                placeholder={
                  language === "javascript"
                    ? "// Access inputs via 'inputs' object\n// Example: const id = inputs.arg1;\n\nreturn inputs.arg1;"
                    : "# Access inputs via 'inputs' dict\n# Example: value = inputs['arg1']\n\nreturn inputs['arg1']"
                }
                spellCheck={false}
                style={{
                  fontFamily: "'Fira Code', 'Cascadia Code', Consolas, monospace",
                  color: "transparent",
                  caretColor: "#1f2937",
                }}
              />
            </div>
            {/* Make selection text visible when selecting */}
            <style>{`
              .code-editor-area textarea::selection {
                background-color: rgba(6, 182, 212, 0.3);
                -webkit-text-fill-color: #1f2937;
                color: #1f2937;
              }
              .code-editor-area textarea::-moz-selection {
                background-color: rgba(6, 182, 212, 0.3);
                color: #1f2937;
              }
            `}</style>
          </div>
        </div>
      </div>
    </WorkflowNodeModal>
  );
};

export default CodeEditorModal;
